import forge from 'apis/forge';
import { AuthUser } from 'dragonmouse-shared/interfaces';
import {
	setLoadingState,
	setSceneLoadError,
	setDesignSystemLoadError,
	updateActiveDesignSystemVersion,
	updateActiveDsChat,
	updateActiveSceneChat,
	updateSceneChatRefs,
	updateActiveSceneDesignSystem,
	updateActiveSceneLinkedDesignSystemVersion,
	updateActiveSceneLatestDesignSystemVersion,
	updateDsChatRefs,
	updateFrames,
	updateGuestFrames,
	updateGuestScenes,
	updateScenes,
	updateUser,
	updateWallet,
} from 'redux/db/actions';
import store from 'redux/store';
import { validate as validateUuid } from 'uuid';
import { ScenePollingStream } from './ScenePollingStream';
import { SceneChatPollingStream } from './SceneChatPollingStream';
import { DesignSystemVersionPollingStream } from './DesignSystemVersionPollingStream';
import { DsChatPollingStream } from './DsChatPollingStream';
import { FramePollingStream } from './FramePollingStream';
import { ProfilePollingStream } from './ProfilePollingStream';
import { SessionPollingStream } from './SessionPollingStream';

class StreamSync {
	// Data to track - currently matches the Redux store
	private _authUserId: string | null = null;
	private _activeSceneId: string | null = null;
	private _activeSceneChatId: string | null = null;
	private _activeDesignSystemId: string | null = null;
	private _activeDsChatId: string | null = null;

	// ** 1 **
	// streams are based on authUserId
	private _profileStream: ProfilePollingStream | null = null;
	private _sessionStream: SessionPollingStream | null = null;

	// ** 2 **
	// streams based on activeSceneId
	private _activeSceneFramesStream: FramePollingStream | null = null;
	private _activeSceneStream: ScenePollingStream | null = null;

	// ** 2b **
	// stream based on activeSceneChatId
	private _activeSceneChatStream: SceneChatPollingStream | null = null;

	// ** 4 **
	// stream based on activeDesignSystemId
	private _activeDesignSystemVersionStream: DesignSystemVersionPollingStream | null = null;

	// ** 5 **
	// stream based on activeDsChatId
	private _activeDsChatStream: DsChatPollingStream | null = null;

	constructor() {
		// These are values in the Redux store that we need to track.
		// For example when the activeSceneId changes from null to a scene ID, we
		// will start streaming the frames and chats and storing them in Redux.
		// Conversely, when the activeSceneId changes to null, we
		// will stop streaming the frames and chats and remove them from Redux.
		store.subscribe(() => {
			this.authUser = store.getState().mode.authUser;
			this.activeSceneId = store.getState().mode.activeSceneId;
			this.activeSceneChatId = store.getState().mode.activeSceneChatId;
			this.activeDesignSystemId = store.getState().mode.activeDesignSystemId;
			this.activeDsChatId = store.getState().mode.activeDsChatId;
		});
	}

	// ** 1 **
	set authUser(authUser: AuthUser | null) {
		if (authUser) {
			if (authUser.id !== this._authUserId) {
				this._authUserId = authUser.id;
				this.onAuthUserIdChanged(authUser?.id ?? null);
			}
		} else {
			if (this._authUserId) {
				this._authUserId = null;
				this.onAuthUserIdChanged(null);
			}
		}
	}

	// ** 2 **
	set activeSceneId(id: string | null) {
		if (id !== this._activeSceneId) {
			this._activeSceneId = id;
			this.onActiveSceneIdChanged(id);
		}
	}

	// ** 2b **
	set activeSceneChatId(id: string | null) {
		if (id !== this._activeSceneChatId) {
			this._activeSceneChatId = id;
			this.onActiveSceneChatIdChanged(id);
		}
	}

	// ** 4 **
	set activeDesignSystemId(id: string | null) {
		if (id !== this._activeDesignSystemId) {
			this._activeDesignSystemId = id;
			this.onActiveDesignSystemIdChanged(id);
		}
	}

	// ** 5 **
	set activeDsChatId(id: string | null) {
		if (id !== this._activeDsChatId) {
			this._activeDsChatId = id;
			this.onActiveDsChatIdChanged(id);
		}
	}

	// ** 1 **
	onAuthUserIdChanged(userId: string | null) {
		this._profileStream?.stop('onAuthUserIdChanged');
		this._sessionStream?.stop('onAuthUserIdChanged');

		if (!userId) {
			// Stop all streams since user is logging out
			this._activeSceneFramesStream?.stop('onAuthUserIdChanged');
			this._activeSceneStream?.stop('onAuthUserIdChanged');
			this._activeSceneChatStream?.stop('onAuthUserIdChanged');
			this._activeDesignSystemVersionStream?.stop('onAuthUserIdChanged');
			this._activeDsChatStream?.stop('onAuthUserIdChanged');

			// clear old scenes and frames -- user Id is null so we should not have access to them
			store.dispatch(updateScenes([]));
			store.dispatch(updateGuestScenes([]));
			store.dispatch(updateGuestFrames([]));
			store.dispatch(updateFrames([]));
			store.dispatch(updateActiveSceneChat(null));
			store.dispatch(updateSceneChatRefs([]));
			store.dispatch(updateActiveSceneDesignSystem(null));
			store.dispatch(updateActiveSceneLinkedDesignSystemVersion(null));
			store.dispatch(updateActiveSceneLatestDesignSystemVersion(null));
			return;
		}

		// Start session stream for guest scenes and frames
		// (this list should only change when page is refreshed, which should also cause userId to change and this to be called again)
		this._sessionStream = new SessionPollingStream();
		this._sessionStream.start();

		if (userId === 'guest') {
			store.dispatch(setLoadingState({ resource: 'profile', state: 'loaded' }));
			store.dispatch(setLoadingState({ resource: 'scenes', state: 'loaded' }));
			store.dispatch(
				updateUser({
					id: 'guest',
					displayName: 'Guest',
					metadata: {},
					writeId: '',
					createdAt: new Date(),
					updatedAt: new Date(),
				})
			);
			store.dispatch(updateWallet(null));
			return;
		}

		store.dispatch(setLoadingState({ resource: 'profile', state: 'loading' }));
		store.dispatch(setLoadingState({ resource: 'scenes', state: 'loading' }));

		forge.fetchWallet().catch((error: any) => {
			console.error('Failed to fetch wallet:', error);
			store.dispatch(updateWallet(null));
		});

		this._profileStream = new ProfilePollingStream();
		this._profileStream.start();
	}

	// ** 2 **
	onActiveSceneIdChanged(activeSceneId: string | null) {
		this._activeSceneFramesStream?.stop('onActiveSceneIdChanged');
		this._activeSceneStream?.stop('onActiveSceneIdChanged');
		store.dispatch(setSceneLoadError(null));

		if (activeSceneId) {
			if (!validateUuid(activeSceneId)) {
				store.dispatch(setSceneLoadError('Invalid scene ID'));
				store.dispatch(updateFrames([]));
				store.dispatch(updateActiveSceneChat(null));
				store.dispatch(updateSceneChatRefs([]));
				store.dispatch(updateActiveSceneDesignSystem(null));
				store.dispatch(updateActiveSceneLinkedDesignSystemVersion(null));
				store.dispatch(updateActiveSceneLatestDesignSystemVersion(null));
				return;
			}

			this._activeSceneFramesStream = new FramePollingStream(activeSceneId);
			this._activeSceneStream = new ScenePollingStream(activeSceneId);
			this._activeSceneFramesStream.start();
			this._activeSceneStream.start();
		} else {
			store.dispatch(updateFrames([]));
			store.dispatch(updateActiveSceneChat(null));
			store.dispatch(updateSceneChatRefs([]));
			store.dispatch(updateActiveSceneDesignSystem(null));
			store.dispatch(updateActiveSceneLinkedDesignSystemVersion(null));
			store.dispatch(updateActiveSceneLatestDesignSystemVersion(null));
		}
	}

	// ** 2b **
	onActiveSceneChatIdChanged(activeSceneChatId: string | null) {
		this._activeSceneChatStream?.stop('onActiveSceneChatIdChanged');
		store.dispatch(updateActiveSceneChat(null));
		store.dispatch(setLoadingState({ resource: 'activeScene.chats', state: 'never-loaded' }));

		if (activeSceneChatId) {
			if (!validateUuid(activeSceneChatId)) {
				console.error('Invalid scene chat ID:', activeSceneChatId);
				return;
			}
			this._activeSceneChatStream = new SceneChatPollingStream(activeSceneChatId);
			this._activeSceneChatStream.start();
		} else {
			this._activeSceneChatStream = null;
		}
	}

	// ** 4 **
	onActiveDesignSystemIdChanged(activeDesignSystemId: string | null) {
		this._activeDesignSystemVersionStream?.stop('onActiveDesignSystemIdChanged');
		store.dispatch(setDesignSystemLoadError(null));
		store.dispatch(updateActiveDesignSystemVersion(null));
		store.dispatch(updateDsChatRefs([]));
		store.dispatch(
			setLoadingState({ resource: 'activeDesignSystemVersion', state: 'never-loaded' })
		);

		if (activeDesignSystemId) {
			if (!validateUuid(activeDesignSystemId)) {
				store.dispatch(setDesignSystemLoadError('Invalid design system ID'));
				return;
			}
			this._activeDesignSystemVersionStream = new DesignSystemVersionPollingStream(
				activeDesignSystemId
			);
			this._activeDesignSystemVersionStream.start();
		}
	}

	// ** 5 **
	onActiveDsChatIdChanged(activeDsChatId: string | null) {
		this._activeDsChatStream?.stop('onActiveDsChatIdChanged');
		store.dispatch(updateActiveDsChat(null));
		store.dispatch(setLoadingState({ resource: 'activeDsChat', state: 'never-loaded' }));

		if (activeDsChatId) {
			if (!validateUuid(activeDsChatId)) {
				console.error('Invalid DS chat ID:', activeDsChatId);
				return;
			}
			this._activeDsChatStream = new DsChatPollingStream(activeDsChatId);
			this._activeDsChatStream.start();
		}
	}
}

new StreamSync();
